<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { mdToHtml, htmlToMd, normaliseMd, splitDoc, joinDoc, replacePart, readQuickTriggers, writeQuickTriggers } from '@/utils/kbMarkdown.helper'
import { faqService } from '@/helpers/services/faq.service'
import { tenantService } from '@/helpers/services/tenant.service'
import { tenantHelper, sharedHelper } from '@/utils'
import { readSheet } from '@/utils/faq-sheet.helper'

/**
 * The knowledge base editor.
 *
 * Two things this screen has to make impossible to miss, because getting either
 * wrong produces a bot that confidently says the wrong thing:
 *
 *   1. SAVING IS NOT PUBLISHING. ai_bot keeps answering from the last ingest.
 *      An author who edits the minimum deposit and walks away has changed
 *      nothing the player sees.
 *   2. A `##` HEADING IS THE UNIT OF RETRIEVAL. The bot may only ever see one
 *      section when it answers, so a section has to stand alone. The live
 *      section count is here so an author sees their chunking instead of
 *      guessing at it.
 */
export default {
  // Every authenticated page renders inside the vertical layout —
  // topbar, sidebar, footer. Without this the view mounts bare at the
  // top-left of a blank page, which is what the missing wrapper looks like.
  components: { Layout, PageHeader, MultiSelect, ckeditor: CKEditor.component },
  page() {
    return { title: i18n.t('module.knowledgeBase') }
  },
  data() {
    return {
      tenants: [],
      tenantId: null,
      docs: [],
      /** The follow-up questions the last publish attached to sections, keyed by doc + heading (faqService.followups). */
      followups: [],
      docId: null,
      form: { title: '', body: '', handoff_after_answer: false },
      /** Same heading in two documents, from the last publish. */
      headingDupes: [],
      sections: [],
      /**
       * Questions this FAQ could not answer.
       *
       * Loaded with the document list, not behind a button: the whole point of
       * writing a knowledge base is to close these, and a gap nobody is shown
       * is a gap nobody closes. Costs one indexed GROUP BY.
       */
      unanswered: [],
      unansweredOpen: false,
      /** A `## question` waiting for the new document to be named. */
      pendingHeading: '',
      /**
       * The new-document row, open only while somebody is naming one.
       *
       * This used to be a native `prompt()`, and a native prompt is a control
       * that can simply stop existing: Chrome suppresses further dialogs for a
       * page once anyone ticks "prevent this page from creating more", and then
       * the button does nothing at all with no error and nothing in the
       * console. It also cannot be styled, cannot be translated, and cannot
       * show the id rule it is asking you to follow.
       */
      newDocId: '',
      newDocOpen: false,
      newDocError: '',
      /** Text read from an imported file, waiting for the id to be confirmed. */
      importedBody: '',
      importedTitle: '',
      /** What the sheet conversion did, shown once the draft is created. */
      importedNote: '',

      /**
       * "Bring documents in from another brand."
       *
       * The first version of this was the other way round: open a document,
       * push it to one brand, repeat. That is the wrong end to stand at — the
       * moment you need it is when a NEW brand is empty and wants most of what
       * an existing one already has, and doing that a document at a time from
       * the other brand's editor is a dozen round trips through the brand
       * picker. You stand in the brand that needs them and tick what to take.
       */
      pullOpen: false,
      pullFromId: null,
      pullDocs: [],
      pullPicked: [],
      pullLoading: false,
      pullBusy: false,
      pullError: '',
      loading: false,
      saving: false,
      publishing: false,
      /** Audit findings that stopped the last publish. See publish(). */
      blocking: [],

      /** The last four-layer report, and the poll that waits for a new one. */
      standard: null,
      standardRunning: false,
      standardTurns: 0,
      standardPoll: null,
      message: '',
      error: '',
      dirty: false,

      editor: ClassicEditor,

      /*
       * ── One question at a time ───────────────────────────────────────
       * `form.body` is still the whole markdown document and still what
       * gets saved. `parts` is that document cut at its `## ` lines, and
       * `joinDoc(parts)` reproduces it byte for byte — so editing one
       * question leaves the other 58 untouched, which matters because the
       * bot quotes this text to a customer verbatim.
       *
       * The answer is edited as HTML and converted back by
       * kbMarkdown.helper: the STORED format stays markdown, because `## ` is
       * ai_bot's retrieval unit and changing it would mean rewriting the
       * chunker, the duplicate-heading check and the verbatim-answer path.
       */
      parts: [],
      openIndex: null,
      qQuery: '',
      draftTitle: '',
      // The primary-answer box for the open question: is it on, and which
      // words trigger it. Read out of the heading on open, written back on
      // save — the author never sees the marker itself.
      draftQuickOn: false,
      draftQuickWords: '',
      /** What the last publish settled on, per section. Read-only; see loadQuickTriggers. */
      quickTriggers: [],
      answerHtml: '',
      /* The markdown as it was loaded. If an author opens a document and
       * saves it without typing, this is what goes back — otherwise the
       * round trip's harmless whitespace normalisation would show up in the
       * change log as a body edit nobody made. */
      originalBody: '',
      /* The plain textarea, still one click away. An author who needs to see
       * or fix the raw `##` can, and a document the rich editor mangles is
       * recoverable rather than lost. */
      sourceMode: false,
      syncTimer: null,
      editorConfig: {
        // Only what kbMarkdown.helper can carry back to markdown. A button
        // for anything else would be an invitation to lose formatting on
        // save.
        toolbar: ['bold', 'link', '|', 'bulletedList', '|', 'undo', 'redo'],
        link: { addTargetToExternalLinks: true },
        /*
         * TextTransformation is the dangerous one: it silently rewrites a
         * straight quote into a curly one, `--` into an en dash, `(c)` into
         * ©. The stored text is quoted to a customer verbatim, so an editor
         * that edits characters nobody typed cannot be here.
         *
         * The rest are features whose output this app cannot store.
         */
        removePlugins: [
          /*
           * No headings in an answer, and not just because nobody should have
           * to pick one from a dropdown.
           *
           * A heading here would convert back to a `## ` line INSIDE a
           * section's body, and the document is cut at `## ` lines — so one
           * stray heading in an answer silently becomes an extra question
           * the author never wrote. Removing the plugin means the editor
           * cannot hold one, and a pasted heading arrives as a paragraph
           * with its text intact.
           */
          'Heading',
          'TextTransformation',
          'Italic',
          'BlockQuote',
          'Table',
          'TableToolbar',
          'MediaEmbed',
          'Indent',
          'IndentBlock',
          'CKFinder',
          'EasyImage',
          'Image',
          'ImageCaption',
          'ImageStyle',
          'ImageToolbar',
          'ImageUpload',
        ],
      },

      /*
       * The self-check. Null until it is run — it embeds every section and
       * makes one model call, so it is a button, never automatic on open. The
       * same reasoning the gap report on Bot health uses.
       */
      audit: null,
      auditing: false,
      auditError: '',
    }
  },
  computed: {
    /** How many questions the document has, which is what the bot can find. */
    sectionCount() {
      return this.parts.filter((p) => p.kind === 'section').length
    },

    /**
     * The rows to draw, carrying their index into `parts` — filtering must
     * not renumber anything, or editing a row after a search would write to
     * the wrong question.
     */
    visibleParts() {
      const q = (this.qQuery || '').toLowerCase()
      return this.parts
        .map((p, index) => ({ ...p, index }))
        .filter((p) => {
          if (!q) return true
          // Search the answer too: an author usually remembers the figure
          // they typed, not the heading somebody else wrote.
          // The marker is not part of the question, so searching "quick"
          // should not return every primary answer.
          return (readQuickTriggers(p.title).text + ' ' + p.body).toLowerCase().includes(q)
        })
    },

    overallBadge() {
      return {
        met: 'badge-success',
        not_met: 'badge-danger',
        incomplete: 'badge-warning',
      }[(this.standard && this.standard.overall) || ''] || 'badge-secondary'
    },

    overallLabel() {
      return {
        met: i18n.t('faq.standardMet'),
        not_met: i18n.t('faq.standardNotMet'),
        incomplete: i18n.t('faq.standardIncomplete'),
      }[(this.standard && this.standard.overall) || ''] || '—'
    },
    breadcrumbItems() {
      return [
        { text: i18n.t('module.bot'), href: '/' },
        { text: i18n.t('module.knowledgeBase'), active: true },
      ]
    },
    /** This screen's own brand list. A knowledge base belongs to exactly one
     *  brand, so it is a single-select, never the "All brands" state. */
    tenantDdl() {
      return this.tenants.map((t) => ({ text: t.name, value: t.tenant_id }))
    },
    /** A real two-line example, so the `## ` + answer shape is visible before
     *  anyone types. */
    bodyPlaceholder() {
      return i18n.t('faq.bodyPlaceholder')
    },
    /** Everywhere documents could be taken FROM — every brand but this one. */
    pullSourceDdl() {
      return this.tenantDdl.filter((o) => Number(o.value) !== Number(this.tenantId))
    },
    /** Ids already here, so the list can mark what would be overwritten. */
    existingIds() {
      return this.docs.map((d) => d.docId)
    },
    /**
     * Chunk preview, computed in the browser from the same rule ai_bot uses
     * (split on `## `, drop a heading with nothing under it). Not authoritative
     * — the server recounts on save — but it updates as you type, which is the
     * point.
     */
    localSections() {
      const out = []
      let title = null
      let buf = []
      const flush = () => {
        if (title !== null && buf.join('\n').trim()) out.push(title)
      }
      for (const line of (this.form.body || '').split('\n')) {
        const m = line.match(/^##\s+(.+)$/)
        if (m) {
          flush()
          title = m[1].trim()
          buf = []
        } else {
          buf.push(line)
        }
      }
      flush()
      return out
    },

    /**
     * Why a line that LOOKS like a heading did not become a section.
     *
     * ── Why this exists ──────────────────────────────────────────────────
     * The counter above was honest but unhelpful. Type `##abcd` and it says
     * "0 section(s): none — every section needs a `## ` heading", which is
     * true, and which an author reads as "but I DID write ##". The space is
     * the whole difference and nothing on screen points at it.
     *
     * The rule is not negotiable — ai_bot splits on the same `/^##\s+/`
     * (core/kb_store.ts), and markdown itself requires the space — so the fix
     * is not to loosen the matcher here. Loosening it would be worse than the
     * confusion: the page would count a section the server then drops, and the
     * author would publish believing it landed. This names the offending line
     * instead.
     *
     * Line numbers are 1-based, because that is what the author is looking at.
     */
    sectionProblems() {
      const lines = (this.form.body || '').split('\n')
      const problems = []

      // Headings first, so "nothing under it" can be reported per heading.
      let openHeading = null
      let openLine = 0
      let body = []
      /* A lone `# Title` on the FIRST heading line is a normal markdown
       * document title, not a section somebody got wrong — every imported
       * file has one. Only a `#` that turns up after headings have started is
       * an author reaching for a section and missing. */
      let seenHeading = false
      const closeHeading = () => {
        if (openHeading !== null && !body.join('\n').trim()) {
          problems.push({
            line: openLine,
            text: i18n.t('faq.problemEmptyHeading', [openLine, openHeading]),
          })
        }
      }

      lines.forEach((line, i) => {
        const n = i + 1

        const ok = line.match(/^##\s+(.+)$/)
        if (ok) {
          closeHeading()
          openHeading = ok[1].trim()
          openLine = n
          body = []
          seenHeading = true
          return
        }

        // `##abcd` — the one everybody hits.
        if (/^#{2}[^#\s]/.test(line)) {
          problems.push({ line: n, text: i18n.t('faq.problemSpace', [n, line.trim()]) })
        } else if (/^#{3,}\s/.test(line)) {
          // `### x` is a sub-heading in markdown and NOT a retrieval unit.
          problems.push({ line: n, text: i18n.t('faq.problemH3', [n, line.trim()]) })
        } else if (/^#\s/.test(line) && seenHeading) {
          problems.push({ line: n, text: i18n.t('faq.problemH1', [n, line.trim()]) })
        }

        if (/^#/.test(line)) seenHeading = true

        body.push(line)
      })
      closeHeading()

      return problems
    },
    canSave() {
      return this.form.title.trim() && this.localSections.length > 0 && !this.saving
    },
    /** Contradictions first, then duplicates — see the store of the report. */
    conflicts() {
      return (this.audit?.overlaps || []).filter((o) => o.verdict === 'conflict')
    },
    duplicates() {
      return (this.audit?.overlaps || []).filter((o) => o.verdict === 'duplicate')
    },
    highIssues() {
      return (this.audit?.issues || []).filter((i) => i.severity === 'high')
    },
    otherIssues() {
      return (this.audit?.issues || []).filter((i) => i.severity !== 'high')
    },
    /** True when the audit ran and found nothing at all. Distinct from "not
     *  run yet", which must not read as a clean bill of health. */
    auditClean() {
      return (
        this.audit !== null &&
        (this.audit.issues || []).length === 0 &&
        (this.audit.overlaps || []).length === 0
      )
    },
  },
  async created() {
    /*
     * A knowledge base belongs to exactly ONE brand, so "All brands" is not a
     * state this screen can render.
     *
     * It used to inherit the top bar's choice and, when that was "All brands",
     * show a warning and disable every button — a dead end whose only exit was
     * a control on the other side of the screen that nothing here pointed at.
     * Now the brand is a picker ON this screen: it starts from the top bar when
     * there is one, falls back to the only brand when there is only one, and
     * otherwise waits with a visible, obvious control rather than a scolding.
     */
    try {
      const res = await tenantService.list()
      this.tenants = res.data || []
      this.tenantId = tenantHelper.current()
      if (this.tenantId === null && this.tenants.length === 1) {
        this.tenantId = this.tenants[0].tenant_id
        tenantHelper.set(this.tenantId)
      }
      if (this.tenantId !== null) {
        await this.loadDocs()
        await this.loadUnanswered()
        await this.loadStandard()
      }
    } catch (e) {
      this.error = this.readError(e)
    }
  },

  /*
   * A poll that outlives the screen keeps hitting the API from a page nobody
   * is looking at, and on a SPA that means forever.
   */
  /*
   * A poll that outlives the screen keeps hitting the API from a page nobody
   * is looking at, and on a SPA that means forever.
   */
  beforeDestroy() {
    this.stopStandardPoll()
  },

  methods: {
    /**
     * The brand changed, from THIS screen's picker.
     *
     * Also written to the shared helper so the top bar agrees — leaving the two
     * disagreeing is how somebody edits one brand's FAQ while the header says
     * another.
     */
    async chooseTenant(id) {
      // `@input`, not `@change`: the component emits the raw id on input and
      // the whole option object on change.
      if (id === this.tenantId) return
      this.tenantId = id === '' || id === undefined || id === null ? null : Number(id)
      tenantHelper.set(this.tenantId)
      this.docId = null
      this.form = { title: '', body: '', handoff_after_answer: false }
      this.docs = []
      this.audit = null
      this.dirty = false
      this.message = ''
      this.unanswered = []
      this.blocking = []
      if (this.tenantId !== null) {
        await this.loadDocs()
        await this.loadUnanswered()
        // Per brand: the cost depends on how many sections carry figures, and
        // a stale "~62 turns" on a brand with a 200-section FAQ is a lie on a
        // button.
        await this.loadStandard()
      } else {
        this.stopStandardPoll()
        this.standard = null
        this.standardTurns = 0
      }
    },

    layerBadge(verdict) {
      return {
        pass: 'badge-success',
        fail: 'badge-danger',
        skipped: 'badge-secondary',
      }[verdict] || 'badge-secondary'
    },

    layerLabel(name) {
      return {
        content: i18n.t('faq.layerContent'),
        retrieval: i18n.t('faq.layerRetrieval'),
        answer: i18n.t('faq.layerAnswer'),
        behaviour: i18n.t('faq.layerBehaviour'),
      }[name] || name
    },

    /**
     * Load the last report and the cost of another, without running anything.
     *
     * Called on open and on every brand change: the price is per brand (it
     * depends on how many sections carry figures), and a stale "~62 turns" on
     * a brand with a 200-section FAQ would be a lie on a button.
     */
    async loadStandard() {
      this.stopStandardPoll()
      this.standard = null
      this.standardRunning = false
      this.standardTurns = 0
      if (!this.tenantId) return
      try {
        const res = await faqService.getStandard(this.tenantId)
        this.standardTurns = res.estimated_turns || 0
        const run = res.data
        if (!run) return
        if (run.status === 'running' && !run.stale) {
          // Somebody else started it, or the page was reloaded mid-run. Pick
          // it up rather than offering to start a second one.
          this.standardRunning = true
          this.startStandardPoll()
        } else if (run.status === 'done') {
          this.standard = run.report
        } else if (run.status === 'error') {
          sharedHelper.alertErrorMessageWithoutCode(i18n.t('faq.fullCheckFailed', [run.error]))
        }
      } catch (e) {
        // A missing report is not an error worth a red bar on a screen whose
        // job is editing the FAQ.
        this.standardTurns = 0
      }
    },

    /**
     * Run the four-layer check.
     *
     * Confirmed with the price in the question, because this is the one button
     * on the screen that spends the brand's allowance — around 60 turns, since
     * layers 3 and 4 ask the bot every section twice and the whole golden set
     * twice.
     */
    async startStandard() {
      const ok = await sharedHelper.confirmQuestion({
        title: i18n.t('faq.fullCheckTitle'),
        text: i18n.t('faq.fullCheckText', [this.standardTurns || 60]),
        confirmText: i18n.t('faq.fullCheckRun'),
      })
      if (!ok) return

      this.error = ''
      this.standard = null
      try {
        await faqService.runStandard(this.tenantId)
        this.standardRunning = true
        this.startStandardPoll()
      } catch (e) {
        // 409 means one is already going, which is not a failure: show it.
        if (e && e.response && e.response.status === 409) {
          this.standardRunning = true
          this.startStandardPoll()
        } else {
          sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
        }
      }
    },

    startStandardPoll() {
      this.stopStandardPoll()
      // Every 5s: the run takes minutes, and a tighter poll would just be more
      // requests for the same answer.
      this.standardPoll = setInterval(async () => {
        try {
          const res = await faqService.getStandard(this.tenantId)
          const run = res.data
          if (!run || (run.status === 'running' && !run.stale)) return
          this.stopStandardPoll()
          this.standardRunning = false
          if (run.status === 'done') {
            this.standard = run.report
              sharedHelper.alertSuccessMessage(
              i18n.t('faq.fullCheckDone', [run.overall === 'met' ? i18n.t('faq.standardMet') : i18n.t('faq.standardNotMet')]),
            )
          } else if (run.status === 'error') {
            sharedHelper.alertErrorMessageWithoutCode(i18n.t('faq.fullCheckFailed', [run.error]))
          } else if (run.stale) {
            sharedHelper.alertErrorMessageWithoutCode(
              i18n.t('faq.fullCheckStale'),
            )
          }
        } catch (e) {
          // Keep polling: a dropped request mid-run is not a failed run.
        }
      }, 5000)
    },

    stopStandardPoll() {
      if (this.standardPoll) {
        clearInterval(this.standardPoll)
        this.standardPoll = null
      }
    },

    async runAudit() {
      if (!this.tenantId) return

      this.auditing = true
      this.auditError = ''

      try {
        const res = await faqService.audit(this.tenantId)
        this.audit = res.data || null
      } catch (e) {
        this.auditError = this.readError(e)
        // Cleared, not left stale: showing an old report next to a failure
        // message invites someone to act on findings that may no longer hold.
        this.audit = null
      } finally {
        this.auditing = false
      }
    },

    async loadUnanswered() {
      try {
        const res = await faqService.unanswered(this.tenantId)
        this.unanswered = res.data || []
      } catch (e) {
        // A missing gap list is a narrower screen, not a broken one.
        this.unanswered = []
      }
    },

    /** Jump from a finding to the document it is about. */
    openDocById(docId) {
      const doc = this.docs.find((d) => d.docId === docId)
      if (doc) this.openDoc(doc)
    },

    /** Re-cut the document. Called whenever form.body changed elsewhere. */
    refreshParts() {
      this.parts = splitDoc(this.form.body || '')
      this.openIndex = null
      this.draftTitle = ''
      this.draftQuickOn = false
      this.draftQuickWords = ''
      this.answerHtml = ''
    },

    /**
     * Open a question, or close the open one.
     *
     * Whatever was being edited is committed first — clicking another row is
     * not a reason to lose what you just typed.
     */
    /**
     * The question as the author wrote it, and whether it is a primary
     * answer. `on` is true for both forms of the marker: words the author
     * typed, and the bare one that leaves the words to the last publish.
     */
    quickOf(p) {
      const q = readQuickTriggers(p.title || '')
      return { ...q, on: q.triggers.length > 0 || /<!--\s*quick\s*(:\s*)?-->/i.test(p.title || '') }
    },

    /** What this question actually answers today: the author's words, else the published ones. */
    quickWordsFor(p) {
      const own = this.quickOf(p).triggers
      return (own.length ? own : this.publishedTriggersFor(p)).join(', ')
    },

    togglePart(index) {
      this.commitOpen()
      if (this.openIndex === index) {
        this.openIndex = null
        return
      }
      const part = this.parts[index]
      if (!part) return
      this.openIndex = index
      const q = this.quickOf(part)
      this.draftTitle = q.text
      this.draftQuickWords = q.triggers.join(', ')
      this.draftQuickOn = q.on
      this.answerHtml = mdToHtml(part.body || '')
    },

    /**
     * Write the open row back into the document.
     *
     * The guard is the point: a row that was opened and not actually changed
     * is left byte-identical. Without it, merely clicking through questions
     * would rewrite each one's whitespace and show in the change log as an
     * edit nobody made.
     */
    commitOpen() {
      if (this.openIndex === null) return
      const part = this.parts[this.openIndex]
      if (!part) return

      // The document is cut at `## ` lines, so a heading marker that somehow
      // reached an answer would appear as a question nobody wrote. The editor
      // cannot produce one (Heading is removed); this is the second lock.
      const answer = htmlToMd(this.answerHtml || '').replace(/^#{1,6}\s+/gm, '')
      // The heading as it will be stored: the question the author typed, plus
      // the primary-answer marker when the box is ticked and has words in it.
      // Ticked with words → those words. Ticked and blank → the bare marker,
      // which tells publish to propose them. Unticked → no marker at all.
      const words = this.draftQuickOn ? (this.draftQuickWords || '').split(',') : []
      const title = this.draftQuickOn && !words.some((w) => w.trim())
        ? `${writeQuickTriggers(this.draftTitle, [])} <!-- quick -->`
        : writeQuickTriggers(this.draftTitle, words)
      const titleSame = title === (part.title || '')
      const bodySame = answer === normaliseMd(part.body || '')
      if (titleSame && bodySame) return

      this.parts = replacePart(this.parts, this.openIndex, {
        title,
        body: answer,
      })
      this.form.body = joinDoc(this.parts)
      this.dirty = true
    },

    /** Debounced: converting on every keystroke is felt on a long answer. */
    onPartEdit() {
      this.dirty = true
      clearTimeout(this.syncTimer)
      this.syncTimer = setTimeout(() => this.commitOpen(), 250)
    },

    addQuestion() {
      this.commitOpen()
      const raw = `## ${i18n.t('faq.newQuestionTitle')}\n`
      this.parts = this.parts.concat([
        { kind: 'section', title: i18n.t('faq.newQuestionTitle'), body: '', raw },
      ])
      this.form.body = joinDoc(this.parts)
      this.dirty = true
      // Clear the filter, or the row just added may not be on screen.
      this.qQuery = ''
      const index = this.parts.length - 1
      this.$nextTick(() => {
        this.togglePart(index)
        this.$nextTick(() => {
          // A new question is appended, so on a 59-question document it opens
          // below the fold. Adding something and being left looking at where
          // it is not reads as nothing having happened.
          const row = this.$el.querySelectorAll('.q-item')[index]
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center', behavior: 'smooth' })
          const input = row && row.querySelector('input[type="text"]')
          if (input) {
            input.focus()
            input.select()
          }
        })
      })
    },

    async deleteQuestion(index) {
      const part = this.parts[index]
      if (!part) return
      const ok = await sharedHelper.confirmDestructive({
        title: i18n.t('faq.deleteQuestion'),
        text: i18n.t('faq.deleteQuestionText', [part.title || '']),
        confirmText: i18n.t('faq.deleteQuestion'),
      })
      if (!ok) return
      if (this.openIndex === index) this.openIndex = null
      this.parts = this.parts.filter((_, i) => i !== index)
      this.form.body = joinDoc(this.parts)
      this.dirty = true
    },

    /** The raw-markdown textarea is the other way to change form.body. */
    onSourceInput() {
      this.dirty = true
    },

    /**
     * Switching to the raw markdown and back.
     *
     * Leaving the rich editor commits whatever it holds first, so the
     * textarea shows what would actually be saved rather than a stale copy.
     */
    toggleSource() {
      if (!this.sourceMode) {
        clearTimeout(this.syncTimer)
        this.commitOpen()
        this.sourceMode = true
      } else {
        // The author may have reordered or retyped anything in there, so the
        // rows are rebuilt from the document rather than trusted.
        this.sourceMode = false
        this.refreshParts()
      }
    },

    /**
     * Start answering one, with the question already in place.
     *
     * The heading IS the question, because that is what the bot matches a
     * player's wording against — and because the alternative is the author
     * retyping, in their own words, a sentence that is sitting right there.
     */
    answerQuestion(q) {
      const heading = `## ${q.question.trim()}\n`
      if (this.docId) {
        this.form.body = `${(this.form.body || '').replace(/\s*$/, '')}\n\n${heading}`
        this.refreshParts()
        this.dirty = true
      } else {
        this.importedBody = ''
        this.importedTitle = ''
        this.startNewDoc()
        this.pendingHeading = heading
      }
      this.unansweredOpen = false
      this.$nextTick(() => {
        // Only in source mode; the rich editor has no textarea to focus.
        const ta = this.$refs.bodyInput
        if (ta && ta.setSelectionRange) {
          ta.focus()
          ta.setSelectionRange(ta.value.length, ta.value.length)
        }
      })
    },

    async loadDocs() {
      this.loading = true
      try {
        const res = await faqService.list(this.tenantId)
        this.docs = res.data || []
        this.error = ''
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.loading = false
      }
      // Best effort and after the list: a brand with no publish yet simply has none.
      this.loadFollowups()
      this.loadQuickTriggers()
    },

    /**
     * The words each question answers outright, as the LAST PUBLISH settled
     * them. For a question whose author typed their own, this just echoes
     * them back; for one they left blank it is the only place the proposed
     * words can be seen.
     */
    async loadQuickTriggers() {
      try {
        const res = await faqService.quickTriggers(this.tenantId)
        this.quickTriggers = (res && res.data) || []
      } catch (e) {
        this.quickTriggers = []
      }
    },

    /** The published trigger words for this section, matched by document and heading. */
    publishedTriggersFor(part) {
      if (!this.docId || part.kind !== 'section') return []
      const title = readQuickTriggers(part.title || '').text
      const row = this.quickTriggers.find(
        (r) => r.docId === this.docId && readQuickTriggers(r.heading || '').text === title,
      )
      return (row && row.triggers) || []
    },

    async loadFollowups() {
      try {
        const res = await faqService.followups(this.tenantId)
        this.followups = res.data || []
      } catch (e) {
        this.followups = []
      }
    },

    /** The follow-up attached to this section at the last publish, if any. Matched by document and heading. */
    followupFor(part) {
      if (!this.docId || part.kind !== 'section') return null
      const title = (part.title || '').trim()
      return this.followups.find((f) => f.docId === this.docId && (f.heading || '').trim() === title) || null
    },

    async removeFollowup(fu) {
      const ok = await sharedHelper.confirmQuestion({
        title: i18n.t('faq.followupRemove'),
        text: i18n.t('faq.followupRemoveText', [fu.question]),
        confirmText: i18n.t('faq.followupRemove'),
      })
      if (!ok) return
      try {
        await faqService.removeFollowup(this.tenantId, fu.chunkId)
        this.followups = this.followups.filter((f) => f.chunkId !== fu.chunkId)
      } catch (e) {
        this.error = this.readError(e)
      }
    },

    async openDoc(d) {
      // sharedHelper, not window.confirm: a suppressed native dialog returns
      // false, so this screen would silently refuse to open anything.
      if (this.dirty) {
        const ok = await sharedHelper.confirmQuestion({
          title: i18n.t('faq.unsavedTitle'),
          text: i18n.t('faq.unsavedText'),
          confirmText: i18n.t('faq.discard'),
        })
        if (!ok) return
      }
      try {
        const res = await faqService.get(this.tenantId, d.docId)
        this.docId = d.docId
        this.form = { title: res.data.title, body: res.data.body, handoff_after_answer: Boolean(res.data.handoffAfterAnswer) }
        this.originalBody = res.data.body || ''
        this.refreshParts()
        this.sections = res.sections || []
        this.dirty = false
        this.message = ''
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      }
    },

    /** Open the inline row and put the cursor in it. */
    startNewDoc() {
      this.newDocOpen = true
      this.newDocId = ''
      this.newDocError = ''
      this.importedBody = ''
      this.importedTitle = ''
      this.importedNote = ''
      this.$nextTick(() => this.$refs.newDocInput && this.$refs.newDocInput.focus())
    },

    cancelNewDoc() {
      this.newDocOpen = false
      this.newDocId = ''
      this.newDocError = ''
      this.importedBody = ''
      this.importedTitle = ''
      this.importedNote = ''
    },

    startPull() {
      this.pullOpen = true
      this.pullFromId = null
      this.pullDocs = []
      this.pullPicked = []
      this.pullError = ''
    },

    cancelPull() {
      this.pullOpen = false
      this.pullFromId = null
      this.pullDocs = []
      this.pullPicked = []
      this.pullError = ''
    },

    /** List what the other brand has, so you can tick rather than guess. */
    async loadPullDocs(id) {
      this.pullFromId = id === '' || id === undefined || id === null ? null : Number(id)
      this.pullDocs = []
      this.pullPicked = []
      this.pullError = ''
      if (this.pullFromId === null) return

      this.pullLoading = true
      try {
        const res = await faqService.list(this.pullFromId)
        this.pullDocs = res.data || []
        // Everything that would not overwrite something here is pre-ticked:
        // setting up a new brand is "take all of it, minus a couple", not
        // "pick twelve one at a time".
        this.pullPicked = this.pullDocs
          .filter((d) => !this.existingIds.includes(d.docId))
          .map((d) => d.docId)
      } catch (e) {
        this.pullError = this.readError(e)
      } finally {
        this.pullLoading = false
      }
    },

    /**
     * Bring the ticked documents in.
     *
     * ── Copies, not links ───────────────────────────────────────────────
     * Each brand's KB is a separate corpus in ai_bot, embedded and versioned on
     * its own. There is no "one document, several brands" to point at, and
     * pretending otherwise would be the worst lie available here: an operator
     * would fix a deposit limit once and believe three brands were corrected.
     *
     * ── Overwrites are confirmed by name, once ──────────────────────────
     * Saving is an upsert, so a matching id on this side would replace what is
     * already here. Those are listed in one question rather than one dialog per
     * document.
     */
    async doPull() {
      if (!this.pullPicked.length) {
        this.pullError = 'Tick at least one document.'
        return
      }

      const source = this.tenants.find((t) => Number(t.tenant_id) === Number(this.pullFromId))
      const sourceName = source ? source.name : this.pullFromId

      const clashes = this.pullPicked.filter((id) => this.existingIds.includes(id))
      if (clashes.length) {
        const ok = await sharedHelper.confirmQuestion({
          title: i18n.t('faq.replaceTitle'),
          text: i18n.t('faq.replaceText', [clashes.length, clashes.join(', ')]),
          confirmText: i18n.t('faq.replace'),
        })
        if (!ok) return
      }

      this.pullBusy = true
      this.pullError = ''
      let done = 0
      try {
        for (const id of this.pullPicked) {
          // One at a time, in order: a partial failure then leaves a knowable
          // state ("4 of 7 came across") instead of an unknown one.
          const doc = await faqService.get(this.pullFromId, id)
          await faqService.save(this.tenantId, id, {
            title: doc.data.title,
            body: doc.data.body,
          })
          done++
        }

        this.cancelPull()
        await this.loadDocs()
        this.message =
          `Brought ${done} document(s) in from ${sourceName} as drafts. They are separate copies — ` +
          `editing them here will not change ${sourceName} — and this brand still has to be published.`
      } catch (e) {
        this.pullError = `${this.readError(e)} (${done} of ${this.pullPicked.length} came across.)`
        await this.loadDocs()
      } finally {
        this.pullBusy = false
      }
    },

    /** The hidden file input, opened by the Import button. */
    pickImport() {
      this.$refs.importInput.click()
    },

    /**
     * Import a FAQ somebody already wrote.
     *
     * ── Why only .md / .txt ─────────────────────────────────────────────
     * What this editor stores IS markdown text, so a text file goes in
     * unchanged and what the author sees is what the bot will chunk. .docx and
     * .pdf would need a parser, and a parser that silently mangles a table or
     * drops a heading is worse than refusing the file — the author would
     * publish something they never read.
     *
     * ── Why it does not overwrite anything ──────────────────────────────
     * The file becomes a NEW draft: the id row opens pre-filled from the
     * filename and nothing is written until Create. An import that replaced
     * whatever happened to be open would be one misclick from flattening a
     * live FAQ.
     */
    async importFile(event) {
      const file = event.target.files && event.target.files[0]
      // Cleared immediately, so picking the same file twice still fires change.
      event.target.value = ''
      if (!file) return

      const name = file.name || ''
      const isSheet = /\.(xlsx|xlsm|xls|csv)$/i.test(name)

      if (!isSheet && !/\.(md|markdown|txt)$/i.test(name)) {
        this.error =
          i18n.t('faq.importWrongType')
        return
      }
      // ~5 MB covers a spreadsheet with images in it; the text that comes out
      // is far smaller.
      if (file.size > 5 * 1024 * 1024) {
        sharedHelper.alertErrorMessageWithoutCode(
          i18n.t('faq.importTooBig'),
        )
        return
      }

      let text
      let note = ''
      try {
        if (isSheet) {
          /*
           * The spreadsheet is the real input. Nobody writes a casino's FAQ in
           * markdown — they fill in the sheet we send, pass it round the CS
           * team, and send it back with sixty rows in it. The conversion is
           * described in utils/faq-sheet.helper.js; what matters here is that
           * rows left BLANK are skipped and counted, because the sheet's own
           * instructions say a blank means "let a human take this" and blank is
           * better than a guess.
           */
          const r = await readSheet(file)
          text = r.markdown
          note =
            i18n.t('faq.importNote', [r.imported, r.sheet]) +
            (r.skipped.length ? ' ' + i18n.t('faq.importSkipped', [r.skipped.length, r.skipped.join('; ')]) : '')
          if (!r.imported) {
            sharedHelper.alertErrorMessageWithoutCode(
              i18n.t('faq.importNoAnswers'),
            )
            return
          }
        } else {
          text = await file.text()
        }
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(e && e.message ? e.message : i18n.t('faq.importReadFail'))
        return
      }

      const stem = name.replace(/\.[^.]+$/, '')

      this.error = ''
      this.importedNote = note
      this.importedBody = text
      // A first `# Title` or `## Heading` is the author's own name for it;
      // fall back to the filename, which is at least something they chose.
      // The filename, for a sheet: its first heading is a question, not a name
      // for the whole document.
      this.importedTitle = isSheet
        ? stem
        : (text.match(/^#{1,2}\s+(.+)$/m) || [])[1]?.trim() || stem

      this.newDocOpen = true
      this.newDocError = ''
      // A slug from the filename, so the common case is "press Create".
      this.newDocId = stem.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
      this.$nextTick(() => this.$refs.newDocInput && this.$refs.newDocInput.focus())
    },

    /**
     * Create the draft.
     *
     * The id is part of the URL ai_bot stores the document under, so it is
     * checked here rather than left for the server to reject after the author
     * has already typed a page. Refusing a name that is already taken matters
     * more than the character rule: saving over an existing document would
     * replace somebody's FAQ with a blank one.
     */
    createDoc() {
      const id = (this.newDocId || '').trim().toLowerCase()

      if (!id) {
        this.newDocError = i18n.t('faq.idRequired')
        return
      }
      if (!/^[a-z0-9_-]+$/.test(id)) {
        this.newDocError = i18n.t('faq.idInvalid')
        return
      }
      if (this.docs.some((d) => d.docId === id)) {
        this.newDocError = i18n.t('faq.idExists', [id])
        return
      }

      this.docId = id
      this.form = this.importedBody
        ? { title: this.importedTitle, body: this.importedBody }
        // A question picked from the unanswered list starts the body, so the
        // author lands on an empty line under it with nothing to retype.
        : { title: '', body: this.pendingHeading || '## \n' }
      this.pendingHeading = ''
      this.sections = []
      this.dirty = true
      // Imported, not saved. Said plainly because the file existing somewhere
      // else is exactly the reason somebody would assume this one is safe.
      this.message = this.importedBody
        ? i18n.t('faq.importedDraft', [this.importedNote])
        : ''
      this.cancelNewDoc()
    },

    async save() {
      // The debounce may still be pending; what gets saved must be what is
      // on screen, not what it held a quarter of a second ago.
      clearTimeout(this.syncTimer)
      if (!this.sourceMode) this.commitOpen()
      this.saving = true
      this.error = ''
      try {
        const res = await faqService.save(this.tenantId, this.docId, this.form)
        // ai_bot's own words. It says "saved but NOT live" and that sentence is
        // the whole reason this screen has two buttons.
        sharedHelper.alertSuccessMessage(res.note || i18n.t('faq.saved'))
        this.originalBody = this.form.body
        this.dirty = false
        await this.loadDocs()
      } catch (e) {
        // An author error ("body has no '## ' section headings") arrives here as
        // a sentence they can act on. Show it verbatim.
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },

    async publish(force = false) {
      if (!force) {
        const ok = await sharedHelper.confirmQuestion({
          title: i18n.t('faq.publishTitle'),
          text: i18n.t('faq.publishText'),
          confirmText: i18n.t('faq.publishConfirm'),
        })
        if (!ok) return
      }
      this.publishing = true
      this.error = ''
      this.blocking = []
      try {
        const res = await faqService.publish(this.tenantId, force)
        this.headingDupes = res.duplicates || []
        sharedHelper.alertSuccessMessage(
          res.gate_overridden
            ? i18n.t('faq.liveNowWithProblems', [res.chunks, res.gate_overridden.blocking])
            : i18n.t('faq.liveNow', [res.chunks]),
        )
      } catch (e) {
        /*
         * The publish gate.
         *
         * A `high` audit finding means the bot is ALREADY answering wrongly
         * or not at all, and today one of them was live: a withdrawal section
         * with a TODO marker still in it, which the bot reads out to players.
         * So the refusal lists what to fix rather than saying it failed, and
         * the override is a second button — available, because a brand with
         * one corrected figure to get live should not be blocked by an
         * unrelated finding, but not the path of least resistance.
         */
        const body = (e && e.response && e.response.data) || {}
        if (body.blocking && body.blocking.length) {
          this.blocking = body.blocking
          this.error = ''
        } else {
          sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
        }
      } finally {
        this.publishing = false
      }
    },

    async publishAnyway() {
      const ok = await sharedHelper.confirmQuestion({
        title: i18n.t('faq.publishAnywayTitle'),
        text: i18n.t('faq.publishAnywayText', [this.blocking.length]),
        confirmText: i18n.t('faq.publishAnyway'),
      })
      if (!ok) return
      await this.publish(true)
    },

    async remove(d) {
      const ok = await sharedHelper.confirmQuestion({
        title: i18n.t('faq.deleteTitle'),
        text: i18n.t('faq.deleteText', [d.title]),
        confirmText: i18n.t('label.delete'),
      })
      if (!ok) return
      try {
        await faqService.remove(this.tenantId, d.docId)
        if (this.docId === d.docId) {
          this.docId = null
          this.form = { title: '', body: '', handoff_after_answer: false }
          this.originalBody = ''
          this.refreshParts()
        }
        await this.loadDocs()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      }
    },

    /**
     * Say what actually went wrong.
     *
     * This used to fall back to "Something went wrong." for everything that was
     * not a server-sent `error` string — and a bug in the page itself (a
     * TypeError reading a field the API does not have) lands exactly there. So
     * the one failure that most needed naming was the one guaranteed to be
     * anonymous, and the author was left to guess whether their work had been
     * lost.
     *
     * Order is deliberate: the server's own sentence first (it is written for
     * the author), then Laravel's field errors, then an HTTP status, and only
     * then the raw exception — which is a developer's sentence, but a specific
     * one, and specific beats polite here.
     */
    readError(e) {
      const res = e && e.response
      const data = res && res.data

      if (data && typeof data.error === 'string') return data.error
      if (data && data.error && typeof data.error === 'object') {
        return Object.values(data.error).flat().join(' ')
      }
      if (data && typeof data.message === 'string') return data.message
      if (res && res.status) return i18n.t('faq.serverAnswered', [res.status])
      if (e && e.message) return e.message

      return i18n.t('faq.somethingWrong')
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.knowledgeBase')" :items="breadcrumbItems" />
    <div>
      <!-- The toolbar is a card, like every other screen's controls. It used to
           be three naked buttons on the page background, which is most of why
           this screen looked unfinished next to the rest of the app. -->
      <div class="card mb-3">
        <div class="card-body py-2 d-flex flex-wrap align-items-center">
          <!-- The brand lives HERE, not only in the top bar. A knowledge base
               belongs to one brand, and "All brands" used to leave this screen
               disabled with the only way out on the opposite side of it. -->
          <div class="brand-pick mr-3 my-1">
            <MultiSelect
              :value="tenantId"
              :options="tenantDdl"
              :multiple="false"
              :allow-empty="false"
              :placeholder="$t('label.brand')"
              @input="chooseTenant"
            />
          </div>

          <div class="vr d-none d-md-block mr-3"></div>

          <button class="btn btn-sm btn-outline-primary mr-2 my-1" :disabled="!tenantId" @click="startNewDoc">
            <i class="mdi mdi-file-plus-outline mr-1"></i>{{ $t('faq.newDocument') }}
          </button>
          <button class="btn btn-sm btn-outline-primary mr-2 my-1" :disabled="!tenantId" @click="pickImport">
            <i class="mdi mdi-upload-outline mr-1"></i>{{ $t('faq.importSheet') }}
          </button>
          <button v-if="pullSourceDdl.length" class="btn btn-sm btn-outline-primary mr-2 my-1"
                  :disabled="!tenantId" @click="startPull">
            <i class="mdi mdi-content-copy mr-1"></i>{{ $t('faq.copyFromBrand') }}
          </button>
          <!-- Hidden, because a styled button that opens the real file picker
               is the only way to get the native picker AND the app's look. -->
          <input ref="importInput" type="file"
                 accept=".xlsx,.xlsm,.xls,.csv,.md,.markdown,.txt"
                 class="d-none" @change="importFile" />
          <button class="btn btn-sm btn-success mr-2 my-1" :disabled="publishing || !tenantId" @click="publish(false)">
            <i class="mdi mdi-publish mr-1"></i>{{ publishing ? $t('faq.publishing') : $t('faq.publish') }}
          </button>
          <button v-if="unanswered.length" class="btn btn-sm btn-outline-danger mr-2 my-1"
                  @click="unansweredOpen = !unansweredOpen">
            <i class="mdi mdi-comment-question-outline mr-1"></i>
            {{ $t('faq.unansweredCount', [unanswered.length]) }}
          </button>
          <button class="btn btn-sm btn-outline-secondary mr-2 my-1" :disabled="auditing || !tenantId" @click="runAudit">
            <i class="mdi mdi-stethoscope mr-1"></i>{{ auditing ? $t('faq.checking') : $t('faq.checkMyFaq') }}
          </button>

          <!--
            The full four-layer check, next to the quick one it extends.

            Two buttons rather than one, because they cost very different
            things. "Check my FAQ" reads the text and is instant and free.
            This one ASKS THE BOT every section twice and the whole golden set
            twice — around 60 real turns against this brand's allowance — so
            the price is on the button, before it is pressed rather than after.

            It is also why there is no nightly version: a scheduled run would
            spend most of a month's allowance proving a FAQ unchanged, and
            leave a report at 7am nobody is on duty to act on. The moment that
            matters is publish, and the content layer guards that as a gate.
          -->
          <button
            class="btn btn-sm btn-outline-primary my-1"
            :disabled="standardRunning || !tenantId"
            @click="startStandard"
          >
            <i class="mdi mdi-clipboard-check-outline mr-1"></i>
            <template v-if="standardRunning">{{ $t('faq.fullCheckRunning') }}</template>
            <template v-else>
              {{ $t('faq.fullCheck') }}<span v-if="standardTurns" class="text-muted"> · {{ $t('faq.turnsApprox', [standardTurns]) }}</span>
            </template>
          </button>

          <!-- Not a tooltip, and not a full-width blue block either: someone
               will edit the deposit limit, close the tab and believe it took
               effect, so it sits next to the button it is about. -->
          <div class="small text-muted ml-auto my-1 pl-3 text-right">
            <i class="mdi mdi-information-outline mr-1"></i>
            <strong>{{ $t('faq.savingNotPublishing') }}</strong>
            {{ $t('faq.answersFromLastPublish') }}
          </div>
        </div>
      </div>

      <div v-if="!tenantId" class="alert alert-warning py-2 small">
        {{ $t('faq.pickBrand') }}
      </div>

      <!-- The gap list. Above the editor, because it is the reason to open
           this screen at all: these are questions real players asked and got
           nothing for. -->
      <div v-if="unansweredOpen && unanswered.length" class="card mb-3">
        <div class="card-header bg-white py-2 d-flex align-items-center">
          <h6 class="mb-0">{{ $t('faq.unansweredTitle') }}</h6>
          <span class="small text-muted ml-2">{{ $t('faq.unansweredHint') }}</span>
          <button type="button" class="btn btn-sm btn-link ml-auto p-0" @click="unansweredOpen = false">
            {{ $t('label.close') }}
          </button>
        </div>
        <div class="card-body p-2">
          <div v-for="q in unanswered" :key="q.question + q.lang"
               class="gap-row d-flex align-items-center">
            <div class="flex-grow-1">
              <span>{{ q.question }}</span>
              <span class="small text-muted ml-2">{{ q.lang }}</span>
              <!-- Asked more than once is the priority signal, so it is the
                   only thing shown in colour. -->
              <b-badge v-if="q.times > 1" variant="warning" class="ml-2">×{{ q.times }}</b-badge>
            </div>
            <button class="btn btn-sm btn-outline-primary" @click="answerQuestion(q)">
              {{ $t('faq.answerThis') }}
            </button>
          </div>
        </div>
      </div>

      <!--
        Outcomes are dialogs now (sharedHelper), the way the user form does it.
        What stays on the page is the standing state: an imported draft that is
        not saved yet, and a load failure. Neither is the result of pressing
        something, and a dialog you dismiss would leave the screen saying
        nothing about why it is empty.
      -->
      <div v-if="message" class="alert alert-success py-2 small">{{ message }}</div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <!--
        The publish gate stopped this publish.

        Listing the sections rather than saying "publish failed": every one of
        these is a specific line in a specific section, and the list is the
        only actionable part. One of them was live on 2026-10-06 — a withdrawal
        section with a TODO marker the bot read out to players.
      -->
      <div v-if="headingDupes.length" class="alert alert-warning py-2 small mb-3">
          <b>{{ $t('faq.dupTitle') }}</b> {{ $t('faq.dupText') }}
          <ul class="mb-0 mt-1">
            <li v-for="dup in headingDupes" :key="dup.heading"><code>## {{ dup.heading }}</code> — {{ $t('faq.dupIn', [dup.docs.join(', ')]) }}</li>
          </ul>
        </div>
        <div v-if="blocking.length" class="alert alert-warning py-2">
        <div class="small font-weight-bold mb-1">
          <i class="mdi mdi-alert-outline mr-1"></i>
          {{ $t('faq.notPublished', [blocking.length]) }}
        </div>
        <ul class="mb-2 pl-3 small">
          <li v-for="(b, i) in blocking" :key="'blk-' + i">
            <strong>{{ b.heading || b.doc_id }}</strong> — {{ b.detail }}
          </li>
        </ul>
        <button class="btn btn-sm btn-light mr-2" @click="blocking = []">{{ $t('faq.fixFirst') }}</button>
        <button class="btn btn-sm btn-outline-danger" :disabled="publishing" @click="publishAnyway">{{ $t('faq.publishAnyway') }}</button>
      </div>

      <!--
        The four-layer report.

        One table, four rows, each with what was measured and what it had to
        beat. The old way of answering "is our FAQ good" was four screens that
        disagreed, so the value here is that it is ONE answer with the number
        beside the bar it had to clear — and the kb_version, so this run can be
        compared with the last one only when they measured the same FAQ.
      -->
      <div v-if="standard" class="card mb-3">
        <div class="card-header bg-white py-2 d-flex align-items-center">
          <h6 class="mb-0">
            {{ $t('faq.fullCheck') }}
            <!-- Three outcomes, not two. `incomplete` means a layer never
                 ran — no behaviour set declared, nothing published — which is
                 neither a pass nor a failure of the bot, and showing it as
                 "not met" would send somebody hunting for a problem that is
                 really a gap in the configuration. A brand-new brand hits this
                 on its first check. -->
            <span class="badge ml-1" :class="overallBadge">{{ overallLabel }}</span>
          </h6>
          <span class="small text-muted ml-auto">
            {{ $t('faq.sectionsCount', [standard.generated_cases]) }} · {{ $t('faq.turnsCount', [standard.turns_spent]) }} ·
            {{ standard.kb_version }}
          </span>
        </div>
        <div class="card-body py-2">
          <table class="table table-sm mb-0 std-table">
            <tbody>
              <tr v-for="l in standard.layers" :key="l.layer">
                <td class="std-mark">
                  <span class="badge" :class="layerBadge(l.verdict)">{{ l.verdict.toUpperCase() }}</span>
                </td>
                <td class="std-name">{{ l.layer }}. {{ layerLabel(l.name) }}</td>
                <td>
                  {{ l.measured }}
                  <span class="text-muted small">({{ $t('faq.need', [l.threshold]) }})</span>
                  <!-- The detail is the actionable part, so it is not behind a
                       toggle: every line names a section somebody has to open. -->
                  <ul v-if="l.detail.length" class="mb-0 mt-1 pl-3 small text-muted std-detail">
                    <li v-for="(d, i) in l.detail" :key="l.layer + '-' + i">{{ d }}</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── The self-check ──────────────────────────────────────────────
           Shown only after it is run. An empty panel sitting here before
           anyone presses the button would read as "checked, all clear",
           which is the one thing it must never say by accident. -->
      <div v-if="auditError" class="alert alert-danger py-2 small">{{ auditError }}</div>

      <div v-if="audit" class="card mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <h5 class="mb-0">{{ $t('faq.faqCheck') }}</h5>
            <span class="small text-muted ml-2">
              {{ $t('faq.auditSummary', [audit.documents, audit.sections, audit.checked_pairs]) }}
            </span>
            <button type="button" class="btn btn-sm btn-link ml-auto" @click="audit = null">{{ $t('faq.hide') }}</button>
          </div>

          <div v-if="audit.note" class="alert alert-warning py-1 px-2 small">{{ audit.note }}</div>

          <div v-if="auditClean" class="alert alert-success py-2 small mb-0">
            {{ $t('faq.auditClean') }}
          </div>

          <!-- Conflicts first. A duplicate is untidy; a contradiction means the
               bot is already telling some players the wrong thing, and no
               telemetry report can see it. -->
          <template v-if="conflicts.length">
            <h6 class="text-danger mt-2">
              <i class="mdi mdi-alert-octagon mr-1"></i>{{ $t('faq.contradictions', [conflicts.length]) }}
            </h6>
            <p class="small text-muted">
              {{ $t('faq.contradictionsHint') }}
            </p>
            <div v-for="(o, i) in conflicts" :key="'c' + i" class="border border-danger rounded p-2 mb-2">
              <div class="small font-weight-bold mb-1">{{ o.reason }}</div>
              <div class="row small">
                <div v-for="side in [o.a, o.b]" :key="side.doc_id + side.heading" class="col-md-6">
                  <button type="button" class="btn btn-link p-0 text-left" @click="openDocById(side.doc_id)">
                    {{ side.doc_id }} › {{ side.heading }}
                  </button>
                  <div class="text-muted">{{ side.excerpt }}…</div>
                </div>
              </div>
            </div>
          </template>

          <template v-if="duplicates.length">
            <h6 class="text-warning mt-3">
              <i class="mdi mdi-content-duplicate mr-1"></i>{{ $t('faq.duplicates', [duplicates.length]) }}
            </h6>
            <p class="small text-muted">
              {{ $t('faq.duplicatesHint') }}
            </p>
            <div v-for="(o, i) in duplicates" :key="'d' + i" class="border rounded p-2 mb-2">
              <div class="small mb-1">{{ o.reason }}</div>
              <div class="row small">
                <div v-for="side in [o.a, o.b]" :key="side.doc_id + side.heading" class="col-md-6">
                  <button type="button" class="btn btn-link p-0 text-left" @click="openDocById(side.doc_id)">
                    {{ side.doc_id }} › {{ side.heading }}
                  </button>
                </div>
              </div>
            </div>
          </template>

          <template v-if="highIssues.length">
            <h6 class="text-danger mt-3">
              <i class="mdi mdi-alert mr-1"></i>{{ $t('faq.needsFixing', [highIssues.length]) }}
            </h6>
            <ul class="small mb-0 pl-3">
              <li v-for="(it, i) in highIssues" :key="'h' + i">
                <button type="button" class="btn btn-link p-0" @click="openDocById(it.doc_id)">
                  {{ it.doc_id }}<span v-if="it.heading"> › {{ it.heading }}</span>
                </button>
                — {{ it.detail }}
              </li>
            </ul>
          </template>

          <template v-if="otherIssues.length">
            <h6 class="text-muted mt-3">{{ $t('faq.worthALook', [otherIssues.length]) }}</h6>
            <ul class="small text-muted mb-0 pl-3">
              <li v-for="(it, i) in otherIssues" :key="'o' + i">
                <button type="button" class="btn btn-link p-0 text-muted" @click="openDocById(it.doc_id)">
                  {{ it.doc_id }}<span v-if="it.heading"> › {{ it.heading }}</span>
                </button>
                — {{ it.detail }}
              </li>
            </ul>
          </template>

          <!-- The "what is missing" half, as a count. The clustered, drafted
               version costs a model call and lives on Bot health, so this
               points at it rather than quietly running it. -->
          <div v-if="audit.unanswered_questions_30d" class="alert alert-secondary py-2 small mt-3 mb-0">
            {{ $t('faq.unanswered30d', [audit.unanswered_questions_30d]) }}
          </div>
        </div>
      </div>

      <!-- Standing in the brand that NEEDS the documents and ticking what to
           take, rather than visiting each document in the other brand and
           pushing it across one at a time. -->
      <div v-if="pullOpen" class="card mb-3">
        <div class="card-header bg-white py-2 d-flex align-items-center">
          <h6 class="mb-0">{{ $t('faq.copyPanelTitle') }}</h6>
          <button type="button" class="btn btn-sm btn-link ml-auto p-0" @click="cancelPull">{{ $t('label.close') }}</button>
        </div>
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center mb-2">
            <div class="copy-pick mr-2 my-1">
              <MultiSelect
                :value="pullFromId"
                :options="pullSourceDdl"
                :multiple="false"
                :placeholder="$t('faq.copyFrom')"
                @input="loadPullDocs"
              />
            </div>
            <span v-if="pullLoading" class="small text-muted my-1">{{ $t('label.loading') }}</span>
          </div>

          <div v-if="pullError" class="alert alert-danger py-2 small">{{ pullError }}</div>

          <div v-if="pullFromId && !pullLoading && !pullDocs.length" class="small text-muted">
            {{ $t('faq.brandNoDocs') }}
          </div>

          <div v-if="pullDocs.length">
            <div v-for="d in pullDocs" :key="d.docId" class="pull-row d-flex align-items-center">
              <b-form-checkbox v-model="pullPicked" :value="d.docId" class="mr-2"></b-form-checkbox>
              <div class="flex-grow-1">
                <span class="font-weight-bold">{{ d.title }}</span>
                <span class="small text-muted ml-2">{{ d.docId }} · {{ $t('faq.sectionsCount', [d.sections]) }}</span>
              </div>
              <!-- Named before you press anything, not confirmed after. -->
              <span v-if="existingIds.includes(d.docId)" class="badge badge-warning">{{ $t('faq.replacesHere') }}</span>
            </div>

            <div class="d-flex align-items-center mt-3">
              <button class="btn btn-sm btn-primary mr-2" :disabled="pullBusy || !pullPicked.length" @click="doPull">
                {{ pullBusy ? $t('faq.copying') : $t('faq.copyIn', [pullPicked.length]) }}
              </button>
              <button class="btn btn-sm btn-light" :disabled="pullBusy" @click="cancelPull">{{ $t('label.cancel') }}</button>
              <span class="small text-muted ml-3">
                {{ $t('faq.copiesNote') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- One card, two columns. The document list and the questions in it are
           the same task, and two separate cards left a tall empty rail beside
           a cramped editor. -->
      <!--
        Documents as tabs, not as a side rail.

        A document is chosen once and then worked inside for a long time, so a
        permanent 400px column spends width continuously on a decision made
        once. A tab strip spends 40px of height, and the questions get the
        whole width back.
      -->
      <div class="card">
        <div class="doc-tabs">
          <button v-for="d in docs" :key="d.docId" type="button" class="doc-tab"
                  :class="{ active: docId === d.docId }" @click="openDoc(d)">
            <span class="doc-tab-name">{{ d.title }}</span>
            <!-- sections, not bytes: this is the number that decides whether
                 the bot can find an answer. -->
            <span class="doc-tab-count" :title="$t('faq.sectionsCount', [d.sections])">{{ d.sections }}</span>
            <span v-if="d.handoffAfterAnswer" class="badge badge-pass ml-1"
                  :title="$t('faq.handoffToggle')">{{ $t('faq.handoffBadge') }}</span>
          </button>

          <!-- Just a plus: the toolbar above already says "New document" in
               words, and repeating it here would be the same button twice. -->
          <button type="button" class="doc-tab doc-tab-add" :title="$t('faq.newDocument')"
                  :disabled="!tenantId" @click="startNewDoc">+</button>

          <span v-if="loading" class="small text-muted align-self-center ml-2">{{ $t('label.loading') }}</span>
          <span v-else-if="!docs.length" class="small text-muted align-self-center ml-2">{{ $t('faq.noDocs') }}</span>
        </div>

        <!-- Where the prompt used to be. In the page, so it cannot be
             suppressed, and so the id rule is readable while you type. -->
        <div v-if="newDocOpen" class="border-bottom p-2 bg-light">
          <div class="input-group input-group-sm" style="max-width: 460px">
            <input ref="newDocInput" v-model="newDocId" class="form-control"
                   placeholder="faq" @keyup.enter="createDoc" @keyup.esc="cancelNewDoc" />
            <div class="input-group-append">
              <button class="btn btn-primary" @click="createDoc">{{ $t('faq.create') }}</button>
              <button class="btn btn-light" @click="cancelNewDoc">{{ $t('label.cancel') }}</button>
            </div>
          </div>
          <div v-if="newDocError" class="small text-danger mt-1">{{ newDocError }}</div>
          <div v-else-if="importedBody" class="small text-muted mt-1">
            {{ $t('faq.nameImported') }}
          </div>
          <div v-else class="small text-muted mt-1">
            {{ $t('faq.docIdHint') }}
          </div>
        </div>

        <div class="d-flex flex-column">
          <div v-if="!docId">
            <div class="text-center text-muted py-5">
              <i class="mdi mdi-file-document-outline d-block mb-2" style="font-size: 32px"></i>
              {{ $t('faq.pickDoc') }}
            </div>
          </div>
          <div v-else class="d-flex flex-column flex-grow-1">
            <div class="panel-head">
              <h6 class="mb-0">{{ docId }}</h6>
              <span v-if="dirty" class="badge badge-warning ml-2">{{ $t('faq.unsaved') }}</span>
            </div>

            <div class="card-body">
              <div class="form-group mb-2">
                <input v-model="form.title" class="form-control" :placeholder="$t('label.name')"
                       @input="dirty = true" />
                <!-- Asked more than once, because the field is unlabelled and
                     sits where a page title would. It is YOUR label for the
                     document, not something the bot reads. -->
                <small class="form-text text-muted">
                  {{ $t('faq.titleHint') }}
                </small>
              </div>
              <!-- Per DOCUMENT, not per question: staff decide by which FAQ a
                   question goes in. Nothing is written into the prose, so the
                   bot cannot read a marker aloud or miss it. -->
              <div class="form-group mb-3 handoff-box">
                <b-form-checkbox v-model="form.handoff_after_answer" switch @change="dirty = true">
                  <b>{{ $t('faq.handoffToggle') }}</b>
                </b-form-checkbox>
                <small class="form-text text-muted">
                  {{ $t('faq.handoffHint') }}
                </small>
              </div>
            <div class="d-flex align-items-center mb-2">
              <strong class="small mr-auto">{{ $t('faq.questionsHeading', [sectionCount]) }}</strong>

              <input v-model.trim="qQuery" type="search" class="form-control form-control-sm q-search mr-2"
                     :placeholder="$t('faq.searchQuestions')" />

              <button class="btn btn-sm btn-outline-primary" @click="addQuestion">
                {{ $t('faq.addQuestion') }}
              </button>

              <!-- The whole document as raw markdown, still one click away.
                   An author who needs to reorder, bulk-edit or repair
                   something the rows cannot express can; nothing here is a
                   one-way door. -->
              <button type="button" class="source-toggle ml-2" @click="toggleSource">
                {{ sourceMode ? $t('faq.rtRich') : $t('faq.rtSource') }}
              </button>
            </div>

            <!-- :placeholder, not placeholder: `&#10;` is not decoded inside a
                 plain attribute here, so the hint literally read
                 "## Deposit&#10;Minimum RM30". Bound, it is a real newline. -->
            <textarea v-if="sourceMode" ref="bodyInput" v-model="form.body" class="form-control code" rows="20"
                      :placeholder="bodyPlaceholder"
                      @input="onSourceInput"></textarea>

            <template v-else>
              <!--
                One row per question, answer behind a click.

                The question is a plain FIELD, not a heading style the author
                has to pick from a dropdown. That was the previous design and
                it asked the wrong thing of the wrong people: a CS agent
                writing an answer should not have to know that `## ` is what
                makes the bot able to find it.
              -->
              <div v-if="!visibleParts.length" class="small text-muted py-3">
                {{ qQuery ? $t('faq.noQuestionMatch') : $t('faq.noQuestionsYet') }}
              </div>

              <div v-else class="q-list">
                <div v-for="p in visibleParts" :key="p.index" class="q-item"
                     :class="{ open: openIndex === p.index, preamble: p.kind === 'preamble' }">
                  <div class="q-head" @click="togglePart(p.index)">
                    <i class="mdi q-caret" :class="openIndex === p.index ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
                    <span class="q-title">
                      {{ p.kind === 'preamble' ? $t('faq.docIntro') : (quickOf(p).text || $t('faq.untitledQuestion')) }}
                      <span v-if="quickOf(p).on" class="badge badge-soft-primary ml-2 q-fu-badge"
                            :title="quickWordsFor(p) ? $t('faq.quickBadgeHint', [quickWordsFor(p)]) : $t('faq.quickAutoPending')">{{ $t('faq.quickBadge') }}</span>
                      <span v-if="followupFor(p)" class="badge badge-soft-info ml-2 q-fu-badge" :title="followupFor(p).question">{{ $t('faq.followupBadge') }}</span>
                    </span>
                    <!-- A button, not an anchor. Deleting a question is an
                         action, and `href="javascript:void(0)"` gives it a
                         link's whole menu — open in a new tab, copy link
                         address — for something that is not a place. -->
                    <button v-if="p.kind === 'section'" type="button" class="q-del"
                            :title="$t('faq.deleteQuestion')" @click.stop="deleteQuestion(p.index)">&times;</button>
                  </div>

                  <div v-if="openIndex === p.index" class="q-body">
                    <template v-if="p.kind === 'section'">
                      <label class="small text-muted mb-1 d-block">{{ $t('faq.questionLabel') }}</label>
                      <input v-model="draftTitle" type="text" class="form-control form-control-sm mb-1"
                             :placeholder="$t('faq.questionPlaceholder')" @input="onPartEdit" />
                      <p class="small text-muted mb-3">{{ $t('faq.questionHint') }}</p>

                      <!-- The primary answer. Ticked, this question is what a
                           customer gets when their whole message is one of
                           these words — the DEPOSIT chip sends exactly
                           "DEPOSIT", so that is the case it exists for. The
                           marker itself is written into the heading on save;
                           the author never sees it. -->
                      <div class="q-quick mb-3">
                        <b-form-checkbox v-model="draftQuickOn" switch @change="onPartEdit">
                          <b>{{ $t('faq.quickToggle') }}</b>
                        </b-form-checkbox>
                        <template v-if="draftQuickOn">
                          <input v-model="draftQuickWords" type="text" class="form-control form-control-sm mt-2"
                                 :placeholder="$t('faq.quickPlaceholder')" @input="onPartEdit" />
                          <!-- Blank is the normal case: the bot proposes the words at
                               publish, and this line is where the author sees what it
                               chose. Typing anything takes over completely. -->
                          <small v-if="draftQuickWords.trim()" class="form-text text-muted">{{ $t('faq.quickHint') }}</small>
                          <small v-else-if="publishedTriggersFor(p).length" class="form-text text-muted">
                            <i class="mdi mdi-auto-fix mr-1"></i>{{ $t('faq.quickAutoChose', [publishedTriggersFor(p).join('、')]) }}
                          </small>
                          <small v-else class="form-text text-muted">
                            <i class="mdi mdi-auto-fix mr-1"></i>{{ $t('faq.quickAutoPending') }}
                          </small>
                        </template>
                        <small v-else class="form-text text-muted">{{ $t('faq.quickOffHint') }}</small>
                      </div>
                    </template>
                    <p v-else class="small text-muted mb-2">{{ $t('faq.docIntroHint') }}</p>
                    <!-- What the bot asks after this answer, decided at the last publish
                         (an eligibility requirement the section states). Shown so the
                         author knows, removable so the author decides. -->
                    <div v-if="followupFor(p)" class="q-fu mb-3">
                      <div class="d-flex align-items-start">
                        <div class="flex-grow-1">
                          <div class="small font-weight-bold mb-1">{{ $t('faq.followupTitle') }} <span class="text-muted font-weight-normal">— {{ followupFor(p).requirement }}</span></div>
                          <div class="small"><span class="text-muted">{{ $t('faq.followupQuestion') }}:</span> {{ followupFor(p).question }}</div>
                          <div class="small"><span class="text-muted">{{ $t('faq.followupWhenMet') }}:</span> {{ followupFor(p).whenMet }}</div>
                          <div class="small"><span class="text-muted">{{ $t('faq.followupWhenNot') }}:</span> {{ followupFor(p).whenNot }}</div>
                          <div class="small text-muted mt-1">{{ $t('faq.followupHint') }}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-danger ml-2" @click="removeFollowup(followupFor(p))">{{ $t('faq.followupRemove') }}</button>
                      </div>
                    </div>

                    <label class="small text-muted mb-1 d-block">{{ $t('faq.answerLabel') }}</label>
                    <!-- One editor instance, on the open row only. Mounting 59
                         would be slow and would keep 59 undo stacks alive. -->
                    <ckeditor :editor="editor" v-model="answerHtml" :config="editorConfig"
                              @input="onPartEdit" />
                  </div>
                </div>
              </div>
            </template>

            <div class="mt-3 p-2 rounded sections small">
              <strong>{{ $t('faq.willRetrieve', [localSections.length]) }}</strong>
              <span v-if="!localSections.length" class="text-danger">
                {{ $t('faq.noSections') }}
              </span>

              <!-- The specific line, not the general rule. "0 sections" plus
                   a rule the author believes they followed is a dead end. -->
              <ul v-if="sectionProblems.length" class="mb-0 mt-2 pl-3 text-danger">
                <li v-for="p in sectionProblems" :key="p.line + p.text">{{ p.text }}</li>
              </ul>
            </div>
            </div>
            <div class="card-footer bg-white d-flex justify-content-end align-items-center editor-actions">
              <span v-if="dirty" class="small text-muted mr-auto">{{ $t('faq.unsaved') }}</span>
              <button class="btn btn-sm btn-primary" :disabled="!canSave" @click="save">
                {{ saving ? $t('faq.saving') : $t('faq.saveDraft') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>

/* The document's own id and state, sitting between the tab strip and its
   content so there is no doubt which tab the panel belongs to. */
.panel-head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f4;
  background: #fff;
}
.panel-head h6 {
  font-size: 13px;
  font-weight: 700;
  color: #5c6478;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* ── Documents as tabs ──────────────────────────────────────────────────
   A document is chosen once and worked inside for a long time, so the
   chooser spends height once instead of width continuously. */
.doc-tabs {
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 8px 10px 0;
  border-bottom: 1px solid #e3e6ed;
  background: #f7f8fb;
  /* Eleven documents is a real brand, so the strip scrolls rather than
     wrapping into a second row that moves everything below it. */
  overflow-x: auto;
  scrollbar-width: thin;
}

.doc-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  max-width: 280px;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  background: transparent;
  color: #5c6478;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 12px;
  margin-bottom: -1px;
  cursor: pointer;
}
.doc-tab:hover { background: #eef1f7; color: #2b2f3a; }

/* The active tab joins the panel below it — that join is what says "what you
   are looking at is this one". */
.doc-tab.active {
  background: #fff;
  color: #2b2f3a;
  border-color: #e3e6ed;
  border-bottom: 1px solid #fff;
}

.doc-tab-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The retrievable-section count, quiet until you look for it. */
.doc-tab-count {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  color: #8a93aa;
  background: #eef1f7;
  border-radius: 9px;
  padding: 1px 6px;
}
.doc-tab.active .doc-tab-count { background: #f1eefc; color: #5a2ea6; }

.doc-tab-add {
  font-size: 16px;
  font-weight: 400;
  color: #8a93aa;
  padding: 5px 11px;
}
.doc-tab-add:disabled { opacity: 0.4; cursor: default; }


.badge-pass { background: #e9ddf7; color: #5a2ea6; font-weight: 700; }
.handoff-box { border: 1px solid #eef0f4; border-radius: 6px; padding: 10px 12px; background: #fafbfd; }
/* The four-layer report: verdict, name, measurement. Fixed narrow columns on
   the left so the four verdicts line up as a column the eye can run down. */
.std-table td {
  border-top: 0;
  vertical-align: top;
  padding: 0.35rem 0.5rem;
}
.std-mark {
  width: 4.5rem;
}
.std-name {
  width: 22rem;
}
.std-detail li {
  word-break: break-word;
}

/* Wide enough for a brand name, narrow enough not to look like the page's
   main control — it is a context switch, not the task. */
.brand-pick { min-width: 200px; }
.copy-pick { min-width: 220px; }
.pull-row { border-bottom: 1px solid #eef0f4; padding: 6px 0; }
.pull-row:last-child { border-bottom: 0; }

/* A hairline between the brand and the actions: they are different kinds of
   thing sharing one row. */
.vr { width: 1px; align-self: stretch; background: #eef0f4; margin: 4px 0; }

.doc { border-bottom: 1px solid #eef0f4; padding: 8px 6px; border-radius: 4px; }
.doc:last-child { border-bottom: 0; }
.doc.active { background: #eef3ff; }
.doc:hover:not(.active) { background: #f7f8fb; }

.code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; line-height: 1.6; }

/* The section count reads as OUTPUT of the box above it, not as another
   paragraph of the page. */
.sections { background: #f7f8fb; border: 1px solid #eef0f4; }

.gap-row { border-bottom: 1px solid #eef0f4; padding: 8px 6px; }
.gap-row:last-child { border-bottom: 0; }
.gap-row:hover { background: #f7f8fb; }

/* ── The rich editor ────────────────────────────────────────────────────
   CKEditor builds its own DOM inside this component, so scoped rules have
   to reach into it. */
::v-deep .ck.ck-toolbar {
  border-color: #e3e6ed;
  border-radius: 6px 6px 0 0;
  background: #fbfcfe;
}
::v-deep .ck.ck-editor__main > .ck-editor__editable {
  border-color: #e3e6ed;
  border-radius: 0 0 6px 6px;
  /*
   * Sized so the WHOLE card fits one screen: the document scrolls inside this
   * box, the page itself does not move, and the toolbar above and the Save
   * button below are therefore always where the author left them.
   *
   * The obvious alternative — let the document grow and scroll the page, with
   * a pinned toolbar like every other editor — does not work here. The theme's
   * `.main-content` sets `overflow: hidden`, which kills `position: sticky`
   * for everything inside it, CKEditor's own toolbar included. Changing that
   * is a layout change for every page in the app, to fix one screen.
   *
   * The subtraction is the chrome above and below: brand row, name field,
   * handoff box, toolbar, the section list and the action bar.
   */
  height: calc(100vh - 560px);
  min-height: 260px;
  overflow-y: auto;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.65;
  color: #2b2f3a;
}
::v-deep .ck.ck-editor__main > .ck-editor__editable.ck-focused {
  border-color: #b9a6e6;
  box-shadow: 0 0 0 2px rgba(90, 46, 166, 0.08);
}

/* `## ` is not a headline, it is the RETRIEVAL UNIT: one heading is one
   thing the bot can find and quote to a customer. CKEditor's default h2 is
   display type at 2.3em, which does two bad things at once — it makes a
   59-question FAQ unreadable, and it dresses the single most structural
   element on the page as decoration.

   Drawn here as a marked boundary instead: small, weighty, with the section
   break visible. The author can see where one answer ends and the next
   begins, which is the thing they actually need to get right. */
::v-deep .ck-content h2 {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
  color: #2b2f3a;
  margin: 1.5rem 0 0.4rem;
  padding: 0.35rem 0.6rem;
  border-left: 3px solid #5a2ea6;
  background: #f7f3fd;
  border-radius: 0 4px 4px 0;
}
::v-deep .ck-content h2:first-child { margin-top: 0.15rem; }

/* The document title. The bot never reads it — the hint under the name field
   says exactly that — so it has no business being the largest thing here. */
::v-deep .ck-content h1 {
  font-size: 17px;
  font-weight: 700;
  color: #6c757d;
  margin: 0 0 0.9rem;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid #eef0f4;
}

::v-deep .ck-content p { margin: 0 0 0.55rem; }
::v-deep .ck-content ul,
::v-deep .ck-content ol { margin: 0 0 0.55rem; padding-left: 1.25rem; }
::v-deep .ck-content li { margin-bottom: 0.12rem; }
::v-deep .ck-content a { color: #5a2ea6; text-decoration: underline; }
::v-deep .ck-content strong { font-weight: 700; color: #1f2430; }

/* The heading menu previews each option in its own style, so without this
   the dropdown inherits the same display sizes and becomes a tall column of
   giant words. */
::v-deep .ck.ck-dropdown__panel .ck-heading_heading1,
::v-deep .ck.ck-dropdown__panel .ck-heading_heading2 {
  font-size: 14px !important;
  font-weight: 700 !important;
}
::v-deep .ck.ck-dropdown__panel .ck-heading_paragraph { font-size: 14px !important; }

/* A context switch between two ways of editing the same thing, not an
   action — so it reads as a link, with enough padding to be hittable. */
.source-toggle {
  background: none;
  border: 0;
  font-size: 12px;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 4px;
  text-decoration: none;
}
.source-toggle:hover { background: #f1f2f6; color: #5a2ea6; text-decoration: none; }

/* Not sticky — it cannot be, see the editable's comment above. It does not
   need to be either: the card is sized to fit, so this row is on screen. */
.editor-actions { border-top: 1px solid #eef0f4; }

/* 247 sections would otherwise push every control below the fold. */
.sections ul { max-height: 190px; overflow-y: auto; margin-bottom: 0; }
.sections li { color: #5c6478; }

/* ── One question per row ───────────────────────────────────────────────
   The question is a FIELD on the row, not a heading style picked from a
   dropdown. The previous design asked a CS agent to know that `## ` is what
   makes the bot able to find an answer, which is not their job. */
.q-search { width: 220px; }

.q-list {
  border: 1px solid #e3e6ed;
  border-radius: 6px;
  /* The list scrolls itself rather than the page. 59 collapsed rows is about
     1800px, which would push Save a screen and a half below the fold — and a
     navigator that makes you leave the thing you are navigating is not one. */
  max-height: calc(100vh - 470px);
  min-height: 260px;
  overflow-y: auto;
}
.q-item + .q-item { border-top: 1px solid #eef0f4; }

.q-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  cursor: pointer;
  background: #fbfcfe;
}
.q-head:hover { background: #f3f5fb; }
.q-item.open > .q-head { background: #f3f0fd; }

.q-caret { color: #8a93aa; font-size: 16px; line-height: 1; }
.q-title {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: #2b2f3a;
  /* A long question keeps the row one line tall, so 59 of them stay a
     scannable column rather than a wall. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.q-item.preamble .q-title { font-weight: 500; color: #6c757d; font-style: italic; }

.q-fu-badge { font-size: 10px; vertical-align: middle; }
.q-fu { background: #f3f0ff; border: 1px solid #ddd6fe; border-radius: 8px; padding: 10px 12px; }
.q-quick { background: #fafbfd; border: 1px solid #eef0f4; border-radius: 8px; padding: 10px 12px; }
.badge-soft-primary { background: #e4edff; color: #2f55d4; font-weight: 700; }
.q-fu .small { line-height: 1.5; }

.q-del {
  background: none;
  border: 0;
  color: #c9ced9;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
  text-decoration: none;
}
.q-head:hover .q-del { color: #a8322c; }

.q-body { padding: 12px 14px 14px 30px; background: #fff; }

/* The answer box: one question's answer, not a whole document. */
.q-body ::v-deep .ck.ck-editor__main > .ck-editor__editable {
  min-height: 150px;
  max-height: 46vh;
  overflow-y: auto;
}
/* No headings inside one answer — the question above IS the heading. */
.q-body ::v-deep .ck-content h1,
.q-body ::v-deep .ck-content h2 {
  font-size: 14px;
  font-weight: 700;
  background: none;
  border: 0;
  padding: 0;
  margin: 0.6rem 0 0.3rem;
}

</style>
